<template>
	<section class="newsletter">
		<div class="row columns">
			<div class="newsletter-content">
				<h2>{{ header }}</h2>
				<div v-parse-links v-html="content" />
				<div class="response-message">
					<p v-if="status === 204">
						{{ $t('optinSuccess') }}
					</p>
					<p v-else-if="status === 409">{{ $t('optinDuplicate') }}</p>
					<p v-else-if="status && status !== 204">{{ $t('optinError') }}</p>
				</div>
			</div>

			<div class="newsletter-fields">
				<div class="input-wrapper">
					<input v-model="email" type="text" :placeholder="$t('formEmail')" />
					<input type="submit" class="button" :value="button" @click="submit" />
				</div>

				<div class="input-bg"></div>
			</div>
		</div>
	</section>
</template>

<script setup lang="ts">
defineProps({
	header: { type: String, default: '' },
	content: { type: String, default: '' },
	button: { type: String, default: '' },
});

const email = ref('');
const status = ref<number | null>(null);
const statusText = ref<string | null>(null);

async function submit() {
	try {
		const response = await $fetch.raw('/api/optinSubmit', {
			method: 'POST',
			body: {
				email: email.value,
				opt_in_form_id: 22744,
			},
		});
		status.value = response.status;
		statusText.value = response.statusText;
	} catch (error: unknown) {
		if (error instanceof Error && 'response' in error && error.response) {
			status.value = (error.response as { status?: number }).status || 500;
			statusText.value = (error.response as { statusText?: string }).statusText || 'Unknown error';
		} else {
			status.value = 500;
			statusText.value = 'Unknown error';
		}
	}
}
</script>

<style lang="scss" scoped>
.newsletter {
	position: relative;
	display: flex;
	background: linear-gradient(to right, var(--contentblock-background-color) 50%, #fff 50%);

	.row {
		position: relative;
		left: 0;
		right: 0;
		z-index: 9;
		margin: auto;
		overflow: visible;
		display: flex;
	}

	.newsletter-content {
		display: flex;
		flex-flow: column;
		align-items: stretch;
		background-color: var(--contentblock-background-color);
		color: white;
		margin: auto;
		padding: 60px 15px;
		flex: 1;
		width: 100%;

		h2 {
			font-size: 40px;
			line-height: 1.3;
			margin-bottom: 30px;
			color: #fff;
		}

		:deep(p) {
			font-size: 14px;
			color: #fff;
			padding-bottom: 0;
		}
	}

	.newsletter-fields {
		display: flex;
		flex-shrink: 0;
		position: relative;
		z-index: 999;
		align-items: center;

		.input-wrapper {
			display: flex;
			align-items: stretch; // Ensures both input and button match in height
			gap: 0; // No space between

			input[type='text'],
			.button {
				height: 85px;
				border-radius: 0;
				border: none;
				display: inline-block;
				font-family: var(--body-font-family);
				margin: 0;
			}

			input[type='text'] {
				padding: 0 15px;
				width: 270px;
				font-size: 18px;
			}

			.button {
				width: 218px;
				padding: 0 45px;
				background-color: var(--cta-color);
				color: #fff;
				font-weight: 500;
				text-transform: uppercase;
				font-size: 16px;
				letter-spacing: 2px;
				cursor: pointer;
				transition: background 0.3s ease;

				&:hover {
					background-color: var(--cta-color-hover);
				}
			}
		}

		.input-bg {
			background-color: var(--contentblock-background-color);
			position: absolute;
			inset: 0 217px 0 0;
			z-index: -1;
		}
	}

	.response-message {
		position: absolute;
		bottom: 0;
	}
}

@media (max-width: 1080px) {
	.newsletter {
		.row {
			flex-wrap: wrap;

			.newsletter-content,
			.newsletter-fields {
				width: 100%;
				align-items: flex-start;
			}

			.newsletter-content {
				margin-right: 20px;

				:deep(p) {
					margin: 0;
				}
			}

			.newsletter-fields {
				justify-content: flex-end;
				padding-bottom: 20px;

				.input-bg {
					inset: 0 20px 0 0;
				}
			}

			.input-wrapper {
				margin-bottom: 20px;
			}
		}

		.response-message {
			position: relative;
		}
	}
}

@media (max-width: 550px) {
	.newsletter {
		background: var(--contentblock-background-color);

		.row {
			.newsletter-content {
				padding: 40px 0;
				margin: 0;

				:deep(p) {
					padding-bottom: 0;
					margin-bottom: 0;
				}

				h2 {
					font-size: 28px;
				}
			}

			.newsletter-fields {
				.input-wrapper {
					flex-direction: column;
					width: 100%;

					input[type='text'],
					.button {
						width: 100%;
						text-align: center;
					}
				}
			}
		}
	}
}
</style>
